import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getModules } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import './ModulesPage.css';

// Tanımlı modül türü, API'den gelen veri yapısına uygun olmalı
interface ModuleItem {
  moduleId: number;
  name: string;
  description: string;
  imageUrl?: string;
  testCount: number;
}

// Her bir modül türü için özel resimler
const moduleImages = {
  'personality': '/personality-test.svg',
  'psychology': '/psychology-test.svg',
  'character': '/character-analysis.svg',
  'intelligence': '/intelligence-test.svg', 
  'sociology': '/sociology-test.svg',
  'default': '/personality-test.svg'
};

// Modül adlarına göre uygun resmi seçme fonksiyonu
const getImageForModule = (moduleName: string): string => {
  const nameLower = moduleName.toLowerCase();
  
  if (nameLower.includes('person') || nameLower.includes('kişilik')) {
    return moduleImages.personality;
  } else if (nameLower.includes('psych') || nameLower.includes('psiko')) {
    return moduleImages.psychology;
  } else if (nameLower.includes('character') || nameLower.includes('karakter')) {
    return moduleImages.character;
  } else if (nameLower.includes('intel') || nameLower.includes('zeka')) {
    return moduleImages.intelligence;
  } else if (nameLower.includes('socio') || nameLower.includes('sosyo')) {
    return moduleImages.sociology;
  }
  
  return moduleImages.default;
};

// Sabit modül verileri (API sorunu durumunda gösterilecek)
const mockModules: ModuleItem[] = [
  {
    moduleId: 1,
    name: 'Personality Assessment',
    description: 'Discover your personality traits and understand your behavioral patterns',
    imageUrl: moduleImages.personality,
    testCount: 2
  },
  {
    moduleId: 2,
    name: 'Psychology Tests',
    description: 'Explore your psychological makeup with scientifically validated tests',
    imageUrl: moduleImages.psychology,
    testCount: 3
  },
  {
    moduleId: 3,
    name: 'Character Analysis',
    description: 'Learn about your character strengths and areas for growth',
    imageUrl: moduleImages.character,
    testCount: 1
  },
  {
    moduleId: 4,
    name: 'General Intelligence',
    description: 'Evaluate your cognitive abilities and problem-solving skills',
    imageUrl: moduleImages.intelligence,
    testCount: 2
  },
  {
    moduleId: 5,
    name: 'Sociology Studies',
    description: 'Understand how you relate to social environments and groups',
    imageUrl: moduleImages.sociology,
    testCount: 1
  }
];

const ModulesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [modules, setModules] = useState<ModuleItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [useLocalData, setUseLocalData] = useState<boolean>(false);

  useEffect(() => {
    const loadModules = async () => {
      try {
        setLoading(true);
        setError(null);
        
        try {
          // API'den veri çekmeyi dene
          const fetchedModules = await getModules();
          
          // API'den gelen veri formatını ModuleItem formatına dönüştür
          const formattedModules: ModuleItem[] = fetchedModules.map(module => {
            // Mevcut dile göre çeviri bul veya İngilizce'ye geri dön
            const translation = module.translationList.find(
              tr => tr.languageCode.toLowerCase() === i18n.language.toLowerCase()
            ) || module.translationList.find(tr => tr.languageCode.toLowerCase() === 'en');
            
            const name = translation?.name || 'Unknown Module';
            
            return {
              moduleId: module.moduleId,
              name: name,
              description: translation?.description || 'No description available',
              imageUrl: getImageForModule(name), // İsme göre uygun görsel seçilir
              testCount: module.testList?.length || 0
            };
          });
          
          setModules(formattedModules);
          setUseLocalData(false);
        } catch (apiError) {
          console.error('API error, using mock data:', apiError);
          setModules(mockModules);
          setUseLocalData(true);
        }
      } catch (err) {
        console.error('Error loading modules:', err);
        setError(t('errorLoadingModules'));
        // Hata durumunda sahte veriyi göster
        setModules(mockModules);
        setUseLocalData(true);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      loadModules();
    } else {
      // Oturum açılmamışsa sahte veriyi gösterme
      setModules([]);
    }
  }, [isAuthenticated, t, i18n.language]);

  if (!isAuthenticated) {
    return (
      <div className="modules-page">
        <div className="error-container">
          <p className="error-message">{t('loginRequired')}</p>
          <div className="auth-options">
            <Link to="/login" className="auth-button login-button">
              {t('login')}
            </Link>
            <Link to="/register" className="auth-button register-button">
              {t('register')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modules-page">
      <div className="page-header">
        <h1>{t('availableModules')}</h1>
        <p className="welcome-message">{t('exploreModules')}</p>
        {useLocalData && (
          <div className="mock-data-notice">
            <p>Using sample data for demonstration. Real API data not available.</p>
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading">{t('loading')}</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="modules-grid">
          {modules.length > 0 ? (
            modules.map((module) => (
              <Link 
                to={`/module/${module.moduleId}`} 
                className="module-card" 
                key={module.moduleId}
              >
                <div className="image-container">
                  <div className="image-overlay"></div>
                  <img 
                    src={module.imageUrl || moduleImages.default} 
                    alt={module.name} 
                    className="module-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null; 
                      target.src = moduleImages.default; // Fallback image
                    }}
                  />
                </div>
                <div className="module-content">
                  <h2>{module.name}</h2>
                  <p>{module.description}</p>
                  <div className="test-count">
                    {module.testCount} {module.testCount === 1 ? t('test') : t('tests')}
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div className="no-modules">{t('noModulesAvailable')}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModulesPage; 